import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Switch, FormControlLabel, Chip, CircularProgress, Alert,
} from '@mui/material';
import api from '../../../api/axiosInterceptor';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Admin-only control: turns the Joint Secretary approval stage on/off.
// While the post is vacant (default), cases flow AC -> CA directly.
// Toggling this on inserts Joint Secretary between AC and CA for every
// case that reaches AC from now on — it does not retroactively move
// cases that already passed through.
const WorkflowSettingsPanel = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const fetchSettings = () => {
    setLoading(true);
    api.get('/excess_payment_mgmt/workflow/settings/')
      .then((res) => setEnabled(!!res.data?.joint_secretary_enabled))
      .catch(() => {
        setSnackbarMsg('Failed to load workflow settings.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleToggle = async (e) => {
    const next = e.target.checked;
    setSaving(true);
    try {
      const res = await api.patch('/excess_payment_mgmt/workflow/settings/', {
        joint_secretary_enabled: next,
      });
      setEnabled(!!res.data?.joint_secretary_enabled);
      setSnackbarMsg(res.data?.message || 'Updated.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMsg(err?.response?.data?.error || 'Failed to update setting.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', p: 3 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Workflow Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Controls that affect how every case moves through the approval chain.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
      ) : (
        <Box
          sx={{
            p: 2.5, borderRadius: 2, border: '1.5px solid', borderColor: enabled ? 'success.light' : 'divider',
            bgcolor: enabled ? 'success.50' : 'grey.50',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Box>
              <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                <Typography fontWeight={700}>Joint Secretary Stage</Typography>
                <Chip
                  size="small"
                  label={enabled ? 'Enabled' : 'Disabled (post vacant)'}
                  color={enabled ? 'success' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
                When enabled, cases route <strong>Assistant Commissioner → Joint Secretary → Competent Authority</strong>.
                When disabled, they go <strong>Assistant Commissioner → Competent Authority</strong> directly, bypassing
                Joint Secretary entirely.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={<Switch checked={enabled} onChange={handleToggle} disabled={saving} />}
              label={saving ? 'Saving…' : (enabled ? 'On' : 'Off')}
              labelPlacement="top"
            />
          </Box>

          {!enabled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This only changes routing going forward — cases already sitting at any stage are unaffected.
            </Alert>
          )}
        </Box>
      )}

      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMsg}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default WorkflowSettingsPanel;